import React, { useState } from 'react'

function fmt(n) {
  n = Math.round(n)
  if (n >= 10000000) return '₹' + (n / 10000000).toFixed(1) + ' Cr'
  if (n >= 100000) return '₹' + (n / 100000).toFixed(1) + ' L'
  if (n >= 1000) return '₹' + n.toLocaleString('en-IN')
  return '₹' + n
}

function ord(n) {
  n = parseInt(n)
  const s = (n === 1 || n === 21 || n === 31) ? 'st' : (n === 2 || n === 22) ? 'nd' : (n === 3 || n === 23) ? 'rd' : 'th'
  return n + s
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function monthLabel(mk) {
  const [yr, mo] = mk.split('-')
  return MONTHS[parseInt(mo) - 1] + ' ' + yr
}

export default function PaymentHistory({ loans, emiPayments }) {
  const [filter, setFilter] = useState('all')

  // Build records
  const records = emiPayments
    .map(p => {
      const loan = loans.find(l => l.id === p.loan_id)
      if (!loan) return null
      return { ...p, loan, monthLabel: monthLabel(p.payment_month) }
    })
    .filter(Boolean)
    .sort((a, b) => b.payment_month.localeCompare(a.payment_month))

  const filtered = filter === 'all' ? records : records.filter(r => r.status === filter)

  // Monthly summary
  const byMonth = {}
  records.forEach(r => {
    if (!byMonth[r.payment_month]) byMonth[r.payment_month] = { label: r.monthLabel, paid: 0, unpaid: 0, na: 0, amt: 0 }
    byMonth[r.payment_month][r.status]++
    if (r.status === 'paid') byMonth[r.payment_month].amt += parseFloat(r.amount_paid || 0)
  })
  const monthKeys = Object.keys(byMonth).sort().reverse()

  const statusColor = { paid: '#4a6741', unpaid: '#b8441a', na: '#3d4a5c' }
  const statusLabel = { paid: '✓ Paid', unpaid: '✗ Unpaid', na: '— N/A' }
  const dotColor = { paid: '#4a6741', unpaid: '#b8441a', na: '#3d4a5c' }

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'paid', label: '✓ Paid' },
    { id: 'unpaid', label: '✗ Unpaid' },
    { id: 'na', label: '— N/A' },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.8rem', marginBottom: '1.4rem' }}>
        <div>
          <h2 style={{ fontFamily: 'Georgia,serif', fontSize: '1.55rem', fontWeight: '700', margin: 0 }}>Payment History</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '0.3rem' }}>Full record of all your EMI payments across all months.</p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              style={{
                fontFamily: 'monospace', fontSize: '0.65rem',
                padding: '0.22rem 0.6rem', borderRadius: '3px',
                border: '1px solid', cursor: 'pointer',
                borderColor: filter === f.id ? '#0d0d0d' : 'var(--border)',
                background: filter === f.id ? '#0d0d0d' : 'transparent',
                color: filter === f.id ? '#f5f0e8' : 'var(--text-muted)',
                transition: 'all 0.15s'
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Transaction List */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>All Transactions</span>
          <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            {records.length === 0 ? 'No payment records yet. Start marking your EMIs each month.' : 'No records match this filter.'}
          </div>
        ) : (
          filtered.map((r, i) => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: '0.85rem 1.4rem',
              borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none',
              transition: 'background 0.15s'
            }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {/* Dot */}
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, background: dotColor[r.status] }} />

              {/* Info */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '500', fontSize: '0.82rem' }}>{r.loan.name}</div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                  {r.monthLabel} &nbsp;·&nbsp; Due: {r.loan.due_day ? ord(r.loan.due_day) + ' of month' : 'N/A'}
                </div>
              </div>

              {/* Right */}
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: '500' }}>
                  {r.status === 'na' ? '—' : fmt(r.amount_paid || r.loan.emi_amount)}
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: statusColor[r.status], marginTop: '0.15rem' }}>
                  {statusLabel[r.status]}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Monthly Summary */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Monthly Summary</span>
        </div>

        {monthKeys.length === 0 ? (
          <div style={{ padding: '2rem', color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>No data yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr>
                  {['Month', 'Paid', 'Unpaid', 'N/A', 'Amt Paid'].map((h, i) => (
                    <th key={h} style={{
                      fontFamily: 'monospace', fontSize: '0.63rem',
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                      color: 'var(--text-muted)', padding: '0.5rem 0.8rem',
                      borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)',
                      textAlign: i === 0 ? 'left' : i === 4 ? 'right' : 'center'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {monthKeys.map(mk => {
                  const m = byMonth[mk]
                  return (
                    <tr key={mk}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.04)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', fontWeight: '500' }}>{m.label}</td>
                      <td style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', textAlign: 'center', fontFamily: 'monospace', color: '#4a6741' }}>{m.paid}</td>
                      <td style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', textAlign: 'center', fontFamily: 'monospace', color: '#b8441a' }}>{m.unpaid}</td>
                      <td style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', textAlign: 'center', fontFamily: 'monospace', color: '#3d4a5c' }}>{m.na}</td>
                      <td style={{ padding: '0.55rem 0.8rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontFamily: 'monospace', fontWeight: '500' }}>{fmt(m.amt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}